// Which tenants a deal's usage comes from.
//
// A binding is what turns a tenant slug on a fact into a deal. Without one the
// facts are still rated, and then dropped — `skipped_unbound` — so a deal with
// no binding is a deal whose usage never reaches its bill. Read-only for now:
// a binding is written by the CLI, and the card exists so the absence of one is
// visible rather than inferred from a figure that is too small.

import type { ReactNode } from "react";
import styles from "./TenantBindingsCard.module.css";
import { Card, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { formatInstantDate } from "../../format/calendarDate.js";
import { useTenantBindings } from "../../api/queries.js";

export function TenantBindingsCard({ dealId }: { dealId: string }): ReactNode {
  const query = useTenantBindings(dealId);

  return (
    <Card className={styles.card}>
      <CardHeader
        title="Tenants"
        description="The deployments and tenants this deal's usage is rated from. A window is half-open: it ends the day the next one starts."
      />
      <QueryBoundary query={query} label="this deal's tenant bindings">
        {(page) => (
          <Table label="Tenant bindings">
            <thead>
              <tr>
                <Th>Deployment</Th>
                <Th>Tenant</Th>
                <Th>From</Th>
                <Th>Until</Th>
              </tr>
            </thead>
            <tbody>
              {page.items.length === 0 ? (
                <EmptyRow
                  colSpan={4}
                  title="No tenant is bound to this deal"
                  description="Usage from an unbound tenant is rated and then dropped, so this deal's meters produce nothing a bill can carry."
                />
              ) : (
                page.items.map((binding) => (
                  <tr key={binding.id}>
                    <Td mono nowrap>
                      {binding.deployment_slug}
                    </Td>
                    <Td>{binding.tenant_slug}</Td>
                    <Td nowrap>{formatInstantDate(binding.effective_from)}</Td>
                    <Td nowrap>
                      {binding.effective_to === null
                        ? "still bound"
                        : formatInstantDate(binding.effective_to)}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </QueryBoundary>
    </Card>
  );
}
